import { environment } from 'environments/environment';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
@Injectable({ providedIn: 'root' })
export class ParrainAnnonceService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = `${environment.apiNest_2}/api/v1`;
    private readonly uploadUrl = `${environment.apiNest_2}/api/v1/upload/service`;
    getAllAnnonces() {
        return this.http.get(`${this.baseUrl}/parrain/annonces`);
    }
    getAnnonceById(id: string) {
        return this.http.get(`${this.baseUrl}/parrain/annonces/${id}`);
    }

    getAnnoncesParUser(userId: string) {
        return this.http.get(`${this.baseUrl}/admin/annonces/user/${userId}`);
    }

    getAllEnAttente() {
        return this.http.get(`${this.baseUrl}/admin/annonces/en-attente`);
    }

    create(dto: any) {
        return this.http.post(`${this.baseUrl}/parrain/annonces`, dto);
    }

    update(id: string, dto: any) {
        return this.http.put(`${this.baseUrl}/parrain/annonces/${id}`, dto);
    }

    submit(id: string) {
        return this.http.post(
            `${this.baseUrl}/parrain/annonces/${id}/soumettre`,
            {}
        );
    }

    archive(id: string) {
        return this.http.post(
            `${this.baseUrl}/parrain/annonces/${id}/archiver`,
            {}
        );
    }

    approve(id: string) {
        return this.http.post(
            `${this.baseUrl}/admin/annonces/${id}/approuver`,
            {},
            { observe: 'response' }
        );
    }

    reject(id: string, commentaire_refus: string) {
        return this.http.post(
            `${this.baseUrl}/admin/annonces/${id}/rejeter`,
            { commentaire_refus },
            { observe: 'response' }
        );
    }

    getApprouvees(): Observable<any[]> {
        return this.http.get<any[]>(`${this.baseUrl}/annonces/approuvees`);
    }

    /**
     * Télécharge une image sur le serveur.
     * @param file Le fichier image à télécharger.
     */
    uploadImage(file: File): Observable<{ url: string }> {
        const formData = new FormData();
        formData.append('files', file);
        formData.append('source', 'photo AnnonceParrain');
        formData.append('typeMedia', 'photo');

        return this.http.post<{ url: string }>(this.uploadUrl, formData);
    }

    /** Vérifie si le user (email) est main sponsor dans le club courant */
    checkIfMainSponsor(
        email: string
    ): Observable<{ is_main_sponsor: boolean }> {
        return this.http.get<{ is_main_sponsor: boolean }>(
            `${this.baseUrl}/sponsors/by-email/${encodeURIComponent(email)}`
        );
    }

    /**
     * Soumet une candidature avec fichiers pour une annonce parrain.
     */
    submitParrainApplication(
        formData: FormData,
        annonceId: string
    ): Observable<any> {
        const url = `${this.baseUrl}/parrain/annonces/apply?annonceId=${annonceId}`;
        return this.http.post(url, formData);
    }

    /**
     * Vérifie si l'utilisateur a déjà postulé à une annonce parrain.
     */
    hasAlreadyApplied(annonceId: string): Observable<{ hasApplied: boolean }> {
        const url = `${this.baseUrl}/parrain/annonces/has-applied/${annonceId}`;
        return this.http.get<{ hasApplied: boolean }>(url);
    }

    /**
     * Récupère les statistiques (clics, vues, candidatures) d'une annonce.
     */
    getParrainAnnonceStats(annonceId: string): Observable<{
        annonceId: string;
        totalApplications: number;
        totalClicks: number;
        totalViews: number;
    }> {
        const url = `${this.baseUrl}/parrain/annonces/${annonceId}/statistiques`;
        return this.http.get<{
            annonceId: string;
            totalApplications: number;
            totalClicks: number;
            totalViews: number;
        }>(url);
    }

    /**
     * Enregistre une interaction utilisateur (VIEW, CLICK) pour une annonce.
     */
    trackParrainInteraction(
        annonceId: string,
        type: 'VIEW' | 'CLICK' | 'APPLY'
    ): Observable<any> {
        const url = `${this.baseUrl}/parrain/annonces/${annonceId}/interactions`;
        return this.http.post(url, { interaction_type: type });
    }
    getAnnonceApprouveeByParrainId(parrainId: string): Observable<any> {
        return this.http.get<any>(
            `${this.baseUrl}/parrain/${parrainId}/annonce-approuvee`
        );
    }

    /**
     * Récupère l'ID du parrain associé à un sponsor.
     * @param sponsorId L'ID du sponsor.
     */
    getParrainIdBySponsorId(sponsorId: string) {
        return this.http
            .get<{ parrainId: string }>(
                `${this.baseUrl}/sponsors/${sponsorId}/parrain`
            )
            .pipe(map((res) => res.parrainId));
    }
}
